// src/components/Layout/Sidebar.js
import React from "react";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { ROLES } from "../../constants";
import { useBadge } from "../../hooks/useBadge";

const menuItems = [
  { path: "/dashboard", icon: "dashboard", label: "Dashboard", roles: [] },
  { path: "/diem-danh", icon: "fact_check", label: "Điểm danh", roles: [ROLES.GVCN, ROLES.GV_PHU] },
  { path: "/nhap-diem", icon: "edit_note", label: "Nhập điểm", roles: [ROLES.GVCN, ROLES.GV_PHU] },
  { path: "/quan-ly-hoc-sinh", icon: "group", label: "Quản lý HS", roles: [ROLES.ADMIN, ROLES.TRUONG_NGANH, ROLES.GVCN, ROLES.GV_PHU] },
  // SỬA: Thêm TRUONG_NGANH và GVCN
  { path: "/quan-ly-nguoi-dung", icon: "manage_accounts", label: "Người dùng", roles: [ROLES.ADMIN, ROLES.TRUONG_NGANH, ROLES.GVCN] },
  { path: "/cai-dat", icon: "settings", label: "Cài đặt", roles: [ROLES.ADMIN] },
  { path: "/bao-cao", icon: "monitoring", label: "Báo cáo", roles: [ROLES.ADMIN, ROLES.TRUONG_NGANH] },
  { path: "/danh-gia-cuoi-ky", icon: "grading", label: "Đánh giá", roles: [ROLES.GVCN, ROLES.ADMIN] },
];

const Sidebar = ({ isOpen, onClose, collapsed, onToggleCollapse }) => {
  const { user, hasRole, logout } = useAuthContext();
  const { darkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const badges = useBadge();

  const filteredMenu = menuItems.filter(item => item.roles.length === 0 || item.roles.some(role => hasRole(role)));

  const handleClick = (e, path) => {
    e.preventDefault();
    if (window.__safeNavigate) window.__safeNavigate(path);
    if (window.innerWidth < 1024) onClose();
  };

  const isActive = (path) => location.pathname === path;

  return (
    <aside className={`fixed top-0 left-0 h-full flex flex-col bg-surface-container border-r border-outline-variant shadow-sm z-[100] transition-all duration-300 lg:translate-x-0
      ${collapsed ? 'w-[72px]' : 'w-64'}
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>

      {/* HEADER */}
      <div className={`flex-shrink-0 p-4 ${collapsed ? 'px-2' : ''}`}>
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <div>
              <h1 className="text-display-header font-bold text-primary leading-tight">MARIA GORETTI</h1>
              <p className="text-body-md text-on-surface-variant mt-0.5">Vân Cương</p>
            </div>
          )}
          {collapsed && <span className="text-2xl">⛪</span>}
          <button onClick={onClose} className="lg:hidden w-9 h-9 rounded-full flex items-center justify-center hover:bg-surface-container transition-colors">
            <span className="material-symbols-outlined text-on-surface-variant">close</span>
          </button>
        </div>
      </div>

      {/* Collapse toggle */}
      <button onClick={onToggleCollapse}
        className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 rounded-full bg-surface-container border border-outline-variant items-center justify-center hover:bg-surface-container-high transition-colors z-10">
        <span className={`material-symbols-outlined text-[14px] text-on-surface-variant transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}>chevron_left</span>
      </button>

      {/* USER INFO */}
      <div className={`flex-shrink-0 mx-4 mb-3 px-3 py-2.5 bg-surface-container-low rounded-xl border border-outline-variant ${collapsed ? 'mx-2 px-2' : ''}`}>
        <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-9 h-9 rounded-full bg-primary-fixed flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
            {user?.hoTen?.charAt(0) || "U"}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-body-md font-semibold text-on-surface truncate">{user?.hoTen || "User"}</p>
              <p className="text-label-caps text-on-surface-variant uppercase truncate">{user?.vaiTro?.join(", ") || "..."}</p>
            </div>
          )}
        </div>
      </div>

      {/* NAVIGATION */}
      <nav className={`flex-1 overflow-y-auto overscroll-contain px-3 space-y-0.5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style]:none [scrollbar-width:none] ${collapsed ? 'px-1' : ''}`}>
        {filteredMenu.map((item) => {
          const active = isActive(item.path);
          const badge = badges[item.path];
          return (
            <a key={item.path} href={item.path} onClick={(e) => handleClick(e, item.path)} title={collapsed ? item.label : ''}
              className={`relative flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 select-none group
                ${collapsed ? 'justify-center px-2' : ''}
                ${active ? 'bg-secondary-container text-on-secondary-container' : 'text-on-surface-variant hover:bg-surface-variant active:bg-surface-container-high'}`}>
              {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-r-full" />}
              {active && <span className="absolute inset-1 bg-primary/5 rounded-lg" />}
              <span className={`material-symbols-outlined flex-shrink-0 relative ${active ? 'text-primary' : ''}`}>{item.icon}</span>
              {!collapsed && <span className="text-body-md truncate flex-1">{item.label}</span>}
              {badge && badge.count > 0 && (
                <span className={`absolute ${collapsed ? 'top-1 right-1' : 'right-3'} min-w-[20px] h-5 flex items-center justify-center px-1.5 rounded-full text-white text-[10px] font-bold leading-none
                  ${badge.type === 'error' ? 'bg-error' : badge.type === 'warning' ? 'bg-[#FF9800]' : 'bg-secondary'}`} title={badge.label}>
                  {badge.count > 99 ? '99+' : badge.count}
                </span>
              )}
            </a>
          );
        })}
      </nav>

      {/* FOOTER Desktop */}
      <div className={`hidden lg:block flex-shrink-0 p-3 border-t border-outline-variant ${collapsed ? 'px-1' : ''}`}>
        <button onClick={logout} title={collapsed ? 'Đăng xuất' : ''}
          className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-on-surface-variant hover:bg-error-container hover:text-on-error-container transition-colors font-medium select-none ${collapsed ? 'justify-center px-2' : ''}`}>
          <span className="material-symbols-outlined flex-shrink-0">logout</span>
          {!collapsed && <span className="text-body-md">Đăng xuất</span>}
        </button>
      </div>

      {/* FOOTER Mobile */}
      <div className="lg:hidden flex-shrink-0 border-t border-outline-variant p-3">
        <button className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-on-surface-variant hover:bg-surface-variant transition-colors font-medium select-none relative">
          <span className="material-symbols-outlined flex-shrink-0 relative">notifications<span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-error rounded-full border-2 border-surface-container"></span></span>
          <span className="text-body-md flex-1">Thông báo</span>
          <span className="min-w-[20px] h-5 flex items-center justify-center px-1.5 rounded-full bg-error text-on-error text-[10px] font-bold leading-none">3</span>
        </button>
        <button onClick={toggleDarkMode} className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-on-surface-variant hover:bg-surface-variant transition-colors font-medium select-none">
          <span className="material-symbols-outlined flex-shrink-0">{darkMode ? 'light_mode' : 'dark_mode'}</span>
          <span className="text-body-md flex-1">{darkMode ? 'Chế độ sáng' : 'Chế độ tối'}</span>
        </button>
        <div className="my-2 border-t border-outline-variant" />
        <button onClick={logout} className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-on-surface-variant hover:bg-error-container hover:text-on-error-container transition-colors font-medium select-none">
          <span className="material-symbols-outlined flex-shrink-0">logout</span><span className="text-body-md">Đăng xuất</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;